import * as React from "react";
import { useRef, useState } from "react";
import { FileText, UploadCloud, X } from "lucide-react";
import { cn } from "../../lib/utils";

export interface FileUploadProps {
  onFileChange: (file: File | null) => void;
  accept?: string;
  label?: string;
  helpText?: string;
  initialFile?: File | null;
  className?: string;
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Single-file drop zone that accepts click or drag-and-drop */
export const FileUpload = React.forwardRef<HTMLDivElement, FileUploadProps>(
  ({ onFileChange, accept = "*/*", label, helpText, initialFile = null, className }, ref) => {
    const [file, setFile] = useState<File | null>(initialFile);
    const [dragging, setDragging] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const select = (next: File | null) => {
      setFile(next);
      onFileChange(next);
    };

    return (
      <div ref={ref} className={cn("space-y-2", className)}>
        {!file ? (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              select(e.dataTransfer.files?.[0] ?? null);
            }}
            className={cn(
              "group flex w-full flex-col items-center rounded-xl border border-dashed px-6 py-7 text-center transition-colors",
              dragging
                ? "border-brand-400 bg-brand-50/60"
                : "border-gray-300 bg-gray-50/50 hover:border-gray-400 hover:bg-gray-50"
            )}
          >
            <span className="mb-3 flex size-10 items-center justify-center rounded-full bg-gray-100 transition-transform">
              <UploadCloud className="size-5 text-gray-500" />
            </span>
            <span className="text-sm font-medium text-gray-800">
              {label || "Click to upload"}{" "}
              <span className="font-normal text-gray-500">or drag and drop</span>
            </span>
            {helpText && <span className="mt-1 text-xs text-gray-500">{helpText}</span>}
            <input
              ref={inputRef}
              type="file"
              accept={accept}
              className="hidden"
              onChange={(e) => {
                select(e.target.files?.[0] ?? null);
                e.target.value = "";
              }}
            />
          </button>
        ) : (
          <div className="flex items-center gap-3 rounded-xl bg-white p-3 ring-1 ring-gray-200">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600">
              <FileText className="size-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-gray-900">{file.name}</p>
              <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
            </div>
            <button
              type="button"
              onClick={() => select(null)}
              aria-label="Remove file"
              className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
            >
              <X className="size-4" />
            </button>
          </div>
        )}
      </div>
    );
  }
);
FileUpload.displayName = "FileUpload";
